import { v4 as uuidv4 } from 'uuid';
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";
import { RPWDataReader } from '../../livedataFuncs';
import { lddGlobalSessionInfo } from '../../Components/navigationTypes';
import { useRef } from 'react';
import { setThirdPartyData } from '../../Components/ThirdPartyList/ThirdPartyFuncs';
import { LoadMunicByName, ResetMunicRecord } from '../../util/rpwPropertyFuncs';
import { runInnerFunction, runInnerFunction2 } from '../../compatManager';
import { setPropertyCoordinate } from '../../redux/reducer';
import { store } from '../../redux/store';
import { parseNameString } from './nameParsing';

export enum UploadStepsEnum {
    BeforeUpload = 1,
    Uploading = 2,
    UploadSuccess = 3,
    UploadFail = 4,
    ProcessingFail = 5
}

export interface IProps {
    datarootContainer: any;
    page: any;
    sidebarshow: any;
    toggleState: any;
}

export interface AnalysisResult {
    id: string;
    status: 'success' | 'error';
    message?: string;
    pdfFileName?: string;
    imageCount?: number;
    fileLocation?: string;
    [key: string]: any; 
}

export interface UploadCallbacks {
    onSuccess: (images: string[]) => void;
    onFailure: (errorCode?: string) => void;
    onProgress?: (currentPage: number, totalPages: number) => void;
}

export interface RenAIResponseData {
    result: Record<string, any>;
    pdfInfo: Record<string, any>;
    processedPages: number;
    scanDate: number; //ms
}

export interface RenAIImportProps {
    show: boolean;
    onHide: () => void;
    responseData: RenAIResponseData;
    base64Content: any;
    LddDocLink: string;
    DataReaderObj: RPWDataReader;
    newFileCreation?: boolean;
    onUpdateDataReaderObj?: (dr: RPWDataReader | null) => void;
}

export interface APSImportSectionProps {
    psm: string;
    sections: Record<string, any>;
    sectionEditableMasks: Record<string, any>;
    setImportInfo: (info: Record<string, any>) => void;
    sectionRefs: any;
    responseData: RenAIResponseData;
    provinceCode: string;
}

export interface SectionProps {
    headers: string[];
    rows: Array<Array<string>>;
    valTypes: Array<Array<string>>;
    editableMask: boolean[][];
    label: string;
    setImportInfo: (info: Record<string, any>) => void;
    onValidationStatusChange?: (label: string, hasErrors: boolean) => void;
}

export interface BoxSectionProps {
    header: string;
    items: string[];
}

export interface SectionRecordCheckboxProps {
    checked: boolean;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    end?: boolean;
}

export interface SectionRecordDatePickerProps {
    date: string;
    setDate?: (date: string) => void;
    placeholder?: string;
    isEdited?: boolean;
    onUndo?: () => void;
    disabled?: boolean;
}

export interface SectionAlertsProps {
    hasVisibleCheckboxes: boolean;
    headerTitle: string;
    label: string;
    isLawyerMatch: boolean;
}

export interface SectionHeaderProps {
    headers: string[];
    hasVisibleCheckboxes: boolean;
    allApplied: boolean;
    onSelectAllChange: (checked: boolean) => void;
}

export interface SectionRecordsProps {
    rows: any[] | null;
    inputValues: any[][];
    valTypes: any[][];
    editableMask: boolean[][];
    isValueEdited: (rowIndex: number, cellIndex: number) => boolean;
    handleInputChange: (rowIndex: number, cellIndex: number, value: string) => void;
    handleFieldUndo: (rowIndex: number, cellIndex: number) => void;
    handleLawyerMatch: () => Promise<void>;
    noImport: boolean[];
    appliedRows: boolean[];
    handleApply: (rowIndex: number) => void;
    handleUndo: (rowIndex: number) => void;
    label: string;
    message: string | null;
    styles: any;
    placeholder: string;
    provinceCode: string;
}

export interface SectionRecordTextProps {
    inputValues: any[][];
    rowIndex: number;
    cellIndex: number;
    cell: string;
    hasBeenEdited: boolean;
    handleInputChange: (rowIndex: number, cellIndex: number, value: string) => void;
    handleFieldUndo: (rowIndex: number, cellIndex: number) => void;
    placeholder: string;
    valType: string;
    label?: string;
    handleLawyerMatch?: () => Promise<void>;
}

export interface SectionRecordDropdownProps {
    inputValues: any[][];
    rowIndex: number;
    cellIndex: number;
    cell: string;
    hasBeenEdited: boolean;
    handleInputChange: (rowIndex: number, cellIndex: number, value: string) => void;
    handleFieldUndo: (rowIndex: number, cellIndex: number) => void;
    placeholder: string;
    options: string[];
}

export interface SectionRecordNoDataProps {
    cell: any;
    styles: any;
    placeholder: string;
    valType: string;
    label: string;
}

export interface SectionRecordErrorMessageProps {
    rowIndex: number;
    cellIndex: number;
    label: string;
    message: string | null;
}

export interface SectionRecordListProps {
    inputValues: any[][];
    rowIndex: number;
    cellIndex: number;
    cell: string;
    hasBeenEdited: boolean;
    handleInputChange: (rowIndex: number, cellIndex: number, value: string) => void;
    handleLawyerMatch: (defaultMatch?: boolean) => Promise<void>;
    handleFieldUndo: (rowIndex: number, cellIndex: number) => void;
    placeholder: string;
}

export const docHeader = "From Document";
export const inFileHeader = "In File";
export const errorPhone = "There must be at least 10 digits in the Phone Number.";
export const errorEmail = "Please enter a valid email address.";
export const errorPrice = "The price must have at least 1 digit.";

// The three values TPAppXML/@PropertyType may hold in ON, matching the Contract Details
// property type select. "" is "--No type of Property--", the default there.
export const ON_PROPERTY_TYPES = ["Freehold (Fee Simple)", "Condo", ""];

// The five values TPAppXML/@PropertyType may hold in AB, matching the Property Description
// "Estate Type" select (see RPWPropertyType.AB). Its blank entry is "Select One", i.e. not a
// selection, so it is not offered here; an unrecognized extraction falls back to Freehold.
export const AB_PROPERTY_TYPES = ["Freehold (Fee Simple)", "Condo", "Leasehold", "Subleasehold", "CondoSubleasehold"];
export const AB_DEFAULT_PROPERTY_TYPE = AB_PROPERTY_TYPES[0];

// Stored @PropertyType value -> label shown on the import screen.
export const PROPERTY_TYPE_DISPLAY_NAMES: Record<string, string> = {
    "Condo": "Condominium (Freehold)",
    "CondoSubleasehold": "Condominium (Subleasehold)",
    "": "--No type of Property--"
};

// A Conveyancer's Instruction Report rides on the 'aps' docType but lists each brokerage's own contact
// details, so the BC listing-to-buyer field copies that suit a BCREA contract must be skipped for it.
export const isConveyancerInstructionReport = (responseData: any): boolean => {
    const displayName = responseData?.result?.docUploadDisplayName;
    return typeof displayName === "string" && displayName.toLowerCase().includes("conveyancer");
}

export class RenAIImportFuncs {

    static getSections(docType: string, DataReaderObj: RPWDataReader, responseData: RenAIResponseData, newFileCreation: boolean) {
        if(docType){
            switch(docType.toLowerCase()) {
                case 'aps':
                    return {
                        buyers: this.getBuyerData(DataReaderObj, responseData, newFileCreation),
                        sellers: this.getSellerData(DataReaderObj, responseData?.result?.Sellers, newFileCreation),
                        otherSideLegalRepresentation: this.getOtherSideLegalRepresentation(responseData, DataReaderObj, DataReaderObj?.getPSM() === 'P' ? 'Seller' : 'Buyer', newFileCreation),
                        propertyData: this.getPropertyData(responseData, DataReaderObj, newFileCreation),
                        priceDepositData: this.getPriceDepositData(responseData, DataReaderObj, newFileCreation),
                        contractData: this.getContractData(responseData, DataReaderObj, newFileCreation),
                        listingAgent: this.getSellerBrokerageData(responseData, DataReaderObj, newFileCreation),
                        coopAgent: this.getBuyerBrokerageData(responseData, DataReaderObj, newFileCreation),
                    }
            }
        }
    }

    static getSectionEditableMasks(docType: string, sections: Record<string, any>) {
        if(docType){
            switch(docType.toLowerCase()) {
                case 'aps':
                    return {
                        buyers: this.generateEditableMask(sections?.buyers?.fields as string[][], [1]),
                        sellers: this.generateEditableMask(sections?.sellers?.fields as string[][], [1]),
                        otherSideLegalRepresentation: this.generateEditableMask(sections?.otherSideLegalRepresentation?.fields as string[][], [1]),
                        propertyData: this.generateEditableMask(sections?.propertyData?.fields as string[][], [1]),
                        priceDepositData: this.generateEditableMask(sections?.priceDepositData?.fields as string[][], [1]),
                        contractData: this.generateEditableMask(sections?.contractData?.fields as string[][], [1]),
                        listingAgent: this.generateEditableMask(sections?.listingAgent?.fields as string[][], [1]),
                        coopAgent: this.generateEditableMask(sections?.coopAgent?.fields as string[][], [1]),
                    }
            }
        }
    }

    static getSectionRefs(docType: string): any {
        if(docType){
            switch(docType.toLowerCase()) {
                case 'aps':
                    return ['buyers', 'sellers', 'otherSide', 'property', 'priceDeposit', 
                            'contract', 'listingAgent', 'coopAgent'].reduce((acc, key) => ({ ...acc, [`${key}Ref`]: useRef<any>(null) }), {});
            }
        }
    }

    static async matchLawyer(SESSION_INFO: lddGlobalSessionInfo, lawyerName: string, lawyerEmail: string) {
        if(!lawyerName || lawyerName.length < 4){
            return false;
        }
        const { appProvinceShort, firm, sid } = SESSION_INFO;
        const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
        try{
            const response = await fetch(`${RWAPI}/matchLawyer?SessionID=${sid}&LawyerName=${lawyerName}&LawyerEmail=${lawyerEmail}`);
            const data = await response.json();
            if(data?.ok && data?.result){
                return data;
            }
            return null;
        } catch(error){
            console.error("[RenAI]: Error matching lawyer", error);
            return null;
        }
        

    }

    static async getAllLawyers(SESSION_INFO: lddGlobalSessionInfo, lawyerName: string) {
        let data = {
            ok: true,
            result: []
        }
        try{
            const trimmedLawyerName = lawyerName.trim().replace(/\s+/g, '');
            const { appProvinceShort, firm, sid } = SESSION_INFO;
            const lawyerSearchString = trimmedLawyerName.length > 3 ? trimmedLawyerName.substring(0, 3) : trimmedLawyerName;
            const response = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}/get/list?SessionID=${sid}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    search: lawyerSearchString,
                    searchType: "AllSolicitor",
                    extraData: {
                        systemFilters: ["System", "User"],
                        sortingObj: ["indexattributevalue", false]
                    }
                })
            });
            data = await response.json();
            return data?.result;
        } catch(error){
            console.error("[RenAI]: Error getting all lawyers", error);
            return [];
        }
        
    }

    static isLawyerMatch(lawyerName: string, lawyerEmail: string, filteredLawyers: any[]) {
        const parser = new DOMParser();
        for(const lawyer of filteredLawyers) {
            const name = lawyer?.Name.trim().replace(/\s+/g, '').toLowerCase();
            const xmlDoc = parser.parseFromString(lawyer.XMLData, "text/xml");
            const solicitorElement = xmlDoc.getElementsByTagName("Solicitor")[0];
            let FirmEmail = solicitorElement?.getAttribute("FirmEmail");
            FirmEmail = FirmEmail.trim().replace(/\s+/g, '').toLowerCase();
            if(name === lawyerName.trim().replace(/\s+/g, '').toLowerCase() && (lawyerEmail === '' || FirmEmail === lawyerEmail.trim().replace(/\s+/g, '').toLowerCase())) {
                return true;
            }
        }
        return false;
    }

    static generateEditableMask(rows: Array<Array<string>>, editableCols: number[] = [1]) {
        return rows?.map(row => row?.map((_, colIdx) => editableCols.includes(colIdx)));
    }
    
    static getBuyerData(DataReaderObj: RPWDataReader, responseData: RenAIResponseData, newFileCreation: boolean): Record<string, Array<Array<string>> | Array<string>> {
        if(DataReaderObj || newFileCreation){
        const docBuyers = responseData?.result?.Buyers;
        const buyerAddr = (DataReaderObj.getAppCode() === "BC" && DataReaderObj.getPSM() === "P")? responseData?.result?.BuyerAddress:"";
        const fileBuyerAddr = DataReaderObj.getTPAppXMLNode()[0]?.attributes.PurchaserAddress;
        const buyersOnFile = DataReaderObj?.getBuyerPeopleInfo() || [];
        const docBuyersArr = docBuyers || [];
        const maxLength = Math.max(buyersOnFile.length, docBuyersArr.length);
        let valTypes = Array.from({ length: maxLength }, () => Array(3).fill("string"));
        let buyerDataArray = Array.from({ length: maxLength }, (_, index) => [
            `Buyer ${index + 1}`,
            docBuyersArr[index] || '',
            buyersOnFile[index]?.Name || ''
        ]);
        if(buyerAddr){
            buyerDataArray.push(["Buyer Address", buyerAddr, fileBuyerAddr]);
            valTypes.push(Array(3).fill("string"));
        }
            return { fields: buyerDataArray, valTypes };
        }
    }
    
    static getSellerData(DataReaderObj: RPWDataReader, docSellers: Array<string>, newFileCreation: boolean): Record<string, Array<Array<string>> | Array<string>> {
        if(DataReaderObj || newFileCreation){
        const sellersOnFile = DataReaderObj?.getSellerPeopleInfo() || [];
        const docSellersArr = docSellers || [];
        const maxLength = Math.max(sellersOnFile.length, docSellersArr.length);
        const valTypes = Array.from({ length: maxLength }, () => Array(3).fill("string"));
        const sellerDataArray = Array.from({ length: maxLength }, (_, index) => [
            `Seller ${index + 1}`,
            docSellersArr[index] || '',
                sellersOnFile[index]?.Name || ''
            ]);
            return { fields: sellerDataArray, valTypes };
        }
    }
    
    static getOtherSideLegalRepresentation(responseData: any, DataReaderObj: RPWDataReader, otherSide: 'Buyer' | 'Seller', newFileCreation: boolean) {
        const appcode = DataReaderObj.getAppCode();
        const headers = ["Name", "Address", "Phone No.", "Email", "Fax"];
        if(responseData?.result && (DataReaderObj || newFileCreation)){
            const addrField = appcode === "MB"? `${otherSide}SolicitorAddress`: `Acknowledgement${otherSide}AddressForService`;
            const docOtherSideName = responseData?.result[`Acknowledgement${otherSide}Lawyer`];
            const docOtherSideAddress =  responseData?.result[addrField];
            const docOtherSidePhoneNumber = responseData?.result[`Acknowledgement${otherSide}PhoneNumber`];
            const docOtherSideEmail = responseData?.result[`Acknowledgement${otherSide}Email`];
            const docOtherSideFax = responseData?.result[`Acknowledgement${otherSide}Fax`];
    
            const inFileOtherSideName = DataReaderObj?.getOtherSideLawyerName();
            const inFileOtherSideAddress = DataReaderObj?.getOtherSideLawyerField("Address");
            const inFileOtherSidePhoneNumber = DataReaderObj?.getOtherSideLawyerField("DirectPhone");
            const inFileOtherSideEmail = DataReaderObj?.getOtherSideLawyerField("FirmEmail");
            const inFileOtherSideFax = DataReaderObj?.getOtherSideLawyerField("Fax");
    
    
            const docArr = [docOtherSideName, docOtherSideAddress, docOtherSidePhoneNumber, docOtherSideEmail, docOtherSideFax];
            const inFileArr = [inFileOtherSideName, inFileOtherSideAddress, inFileOtherSidePhoneNumber, inFileOtherSideEmail, inFileOtherSideFax];
            const valTypes = headers.map((_, index) => {
                if (index === 0) return ["string", "lawyerlist", "string"];
                if (index === 2 || index === 4) return ["string", "phone", "phone"];
                if (index === 3) return ["string", "email", "email"];
                return Array(3).fill("string");
            });
    
            return { fields: headers.map((header, index) => [
                    header || '',
                    docArr[index] || '',
                    inFileArr[index] || '']), valTypes };
        }
        
    }
    
    static getPropertyData(responseData: any, DataReaderObj: RPWDataReader, newFileCreation: boolean) {
        const appcode = DataReaderObj.getAppCode();
        if(DataReaderObj || newFileCreation){
            const pref = ["SK", "NS"].includes(appcode)?"Civic":"";
            if(appcode === "MB"){ // Parse address for MB since the AI can't read it properly on legacy form
                const civicAddr = responseData?.result?.CivicAddress;
                const secs = runInnerFunction("ParseAddressCanadian", civicAddr);
                let fspace = secs[0].search(" ");
                responseData.result.StreetNum = secs[0].substring(0, fspace);
                responseData.result.StreetName = secs[0].substring(fspace + 1);
                responseData.result.City = secs[1];
                responseData.result.PostalCode = secs[3];
            }
            let docUnitType = responseData?.result?.UnitType;
            const inFileUnitType = DataReaderObj?.getUnitType();
            let docUnitNum = responseData?.result?.UnitNum;
            const inFileUnitNum = DataReaderObj?.getUnitNumber();
            const docStreetNumber = responseData?.result[`${pref}StreetNum`];
            const inFileStreetNumber = DataReaderObj?.getStreetNumber();
            const docStreetName = responseData?.result[`${pref}StreetName`];
            const inFileStreetName = DataReaderObj?.getStreetName();
            const docStreetNumberSuffix = responseData?.result[`${pref}Suffix`];
            const inFileStreetNumberSuffix = DataReaderObj?.getStreetNumberSuffix();
            const docPostalCode = responseData?.result[`${pref}PostalCode`];
            const inFilePostalCode = DataReaderObj?.getPostalCode();
            const docCity = responseData?.result[`${pref}City`];
            const inFileCity = DataReaderObj?.getCity();
            let docUploadPropertyType = responseData?.result?.docUploadPropertyType;
            let inFileUploadPropertyType = DataReaderObj?.getPropertyType();
            if(inFileUploadPropertyType && PROPERTY_TYPE_DISPLAY_NAMES[inFileUploadPropertyType]){
                inFileUploadPropertyType = PROPERTY_TYPE_DISPLAY_NAMES[inFileUploadPropertyType];
            }
            let extraHeaders = [], docExtras = [], inFileExtras = [], valTypeExtras = [];
            
            if(DataReaderObj.getAppCode() === "NS" && responseData?.result?.docUploadDisplayName === "NSREC Form 403"){
                extraHeaders.push("New Home");
                docExtras.push("true");
                inFileExtras.push(DataReaderObj.getNewHome() === "0"? "true": "");
                valTypeExtras.push(["string", "checkbox", "checkbox"]);
            }
            if(DataReaderObj.getAppCode() === "BC"){
                extraHeaders.push("PID");
                let responsePID = responseData?.result?.PID;
                let nums = responsePID.replace(/[^0-9]/g, '');
                let PIDString = nums.length < 9?"":`${nums.slice(0, 3)}-${nums.slice(3, 6)}-${nums.slice(6, 9)}`
                docExtras.push(PIDString);
                inFileExtras.push(DataReaderObj.getPID());
                valTypeExtras.push(["string", "string", "string"])
                if(docUnitNum){
                    docUploadPropertyType = "Condo";
                    docUnitType = "Suite";
                }
            }

            if (appcode === "ON") {
                // docUploadPropertyType is free text from the model - OREA Form 110 returns
                // its 51 character title block - and @PropertyType is nvarchar(25), so an
                // off-list value fails the save with "String or binary data would be
                // truncated". Keep only a type ON models; anything else falls to
                // "--No type of Property--" for the lawyer to choose.
                const extracted = String(docUploadPropertyType ?? "").trim().toLowerCase();
                docUploadPropertyType = ON_PROPERTY_TYPES.find(type => type !== "" && type.toLowerCase() === extracted) ?? "";
            }

            if (appcode === "AB") {
                // Only the AREA Residential Resale Condominium Property Purchase Contract is a
                // condo; every other AREA form is freehold. Coerce anything off-list (free text,
                // "", a stale prompt) to a value the AB Estate Type select accepts so the save
                // never fails on @PropertyType, and so the lawyer can still change it here.
                const extracted = String(docUploadPropertyType ?? "").trim().toLowerCase();
                docUploadPropertyType = AB_PROPERTY_TYPES.find(type => type.toLowerCase() === extracted
                        || PROPERTY_TYPE_DISPLAY_NAMES[type]?.toLowerCase() === extracted)
                    ?? (extracted.includes("condo") ? "Condo" : AB_DEFAULT_PROPERTY_TYPE);
            }

            const headers = ["Unit Type", "Unit Number", "Street Number", "Suffix", "Street Name", "City/Town", "Postal Code", "Property Type", ...extraHeaders];
            const docArr = [docUnitType, docUnitNum, docStreetNumber, docStreetNumberSuffix, docStreetName, docCity, docPostalCode, docUploadPropertyType, ...docExtras];
            const inFileArr = [inFileUnitType, inFileUnitNum, inFileStreetNumber, inFileStreetNumberSuffix, inFileStreetName, inFileCity, inFilePostalCode, inFileUploadPropertyType, ...inFileExtras];
    
            const valTypes = [...Array.from({ length: 8 }, () => Array(3).fill("string")), ...valTypeExtras];
            valTypes[7] = ["string", "dropdown", "string"];
    
            return { fields: headers.map((header, index) => [ header, docArr[index] || '', inFileArr[index] || '']), valTypes };
        }
    }
    
    static getPriceDepositData(responseData: any, DataReaderObj: RPWDataReader, newFileCreation: boolean) {
        if(DataReaderObj || newFileCreation){
        let docPurchasePrice;
        try {
            docPurchasePrice = runInnerFunction2("FormatMoneyDataType", [responseData?.result?.PurchasePrice, 'delimited']);
        } catch (e) {
            // inner frame not ready; keep raw price
            console.log("renaiFuncs: FormatMoneyDataType failed for PurchasePrice, keeping raw price", e);
            docPurchasePrice = responseData?.result?.PurchasePrice;
        }
        const rawDocDepositAmounts = responseData?.result?.Deposit?.slice(0, DataReaderObj.getAppCode() === "BC"?1:undefined) ?? [];
        const docDepositAmounts = rawDocDepositAmounts.map((deposit: any) => {
            const rawAmount = (typeof deposit === 'object' && deposit !== null && 'Amount' in deposit) ? deposit.Amount : deposit;
            try {
                return runInnerFunction2("FormatMoneyDataType", [rawAmount, 'delimited']) || '';
            } catch (e) {
                // inner frame not ready; keep raw deposit amount
                console.log("renaiFuncs: FormatMoneyDataType failed for deposit, keeping raw amount", e);
                return rawAmount || '';
            }
        });
        
        let docHSTApplicable = "";
        if(responseData?.result?.HSTIncluded) {
            if(responseData?.result?.HSTIncluded === "included in") {
                docHSTApplicable = "true";
            } else if(responseData?.result?.HSTIncluded === "in addition to") {
                docHSTApplicable = "false";
            }
        }
        // Alberta forms are always not HST included
        if(DataReaderObj.getAppCode() === "AB") docHSTApplicable = "false";

        const inFilePurchasePrice = DataReaderObj?.getContractPrice();
        let inFileDepositAmounts = DataReaderObj?.getDeposits() || [];
        inFileDepositAmounts = inFileDepositAmounts.map((deposit: any) => {
            if (typeof deposit === 'object' && deposit !== null && 'Amount' in deposit) {
                return deposit.Amount;
            }
            return deposit || '';
        });
        const inFileHSTApplicable = DataReaderObj?.getHSTApplicable() === "1"?"true":"false";

        const maxDeposits = Math.max(docDepositAmounts.length, inFileDepositAmounts.length);
        const depositHeaders = Array.from({ length: maxDeposits }, (_, i) => `Deposit ${i + 1}`);
        const taxHeader = `${["AB", "BC", "MB"].indexOf(DataReaderObj.getAppCode()) > -1? "GST": "HST"} Applicable`
        // const taxHeader = "HST Applicable"
        const headers = ["Purchase Price", taxHeader, ...depositHeaders];
        const docArr = [docPurchasePrice, docHSTApplicable, ...docDepositAmounts];
        const inFileArr = [inFilePurchasePrice, inFileHSTApplicable, ...inFileDepositAmounts];
        const valTypes = Array.from({ length: headers.length }, () => Array(3).fill("string"));
        valTypes[0] = ["string", "money", "money"];
        valTypes[1] = ["string", "checkbox", "checkbox"];
        for(let i = 0; i < maxDeposits; i++) {
            valTypes[2+i] = ["string", "money", "money"];
        }
        // HST row hidden for ON only — RenAI still extracts HSTIncluded and the value
        if (DataReaderObj.getAppCode() === "ON") {
            headers.splice(1, 1);
            docArr.splice(1, 1);
            inFileArr.splice(1, 1);
            valTypes.splice(1, 1);
        }
        return { fields: headers.map((header, index) => [ header, docArr[index] || '', inFileArr[index] || '']), valTypes };
        }
    }
    
    static getContractData(responseData: any, DataReaderObj: RPWDataReader, newFileCreation: boolean) {
        const appCode = DataReaderObj?.getAppCode();
        const contractHeaders = (appCode: string) => {
            switch(appCode){
                case "BC":
                    return ["Contract Date", "Closing Date", "Possession Date", "Conditional Until Date"];
                case "MB":
                    return ["Offer to Purchase Date", "Closing Date", "Possession Date", "Conditional Until Date"];
                case "SK":
                    return ["Contract Date", "Possession Date", "", "Conditional Until Date"];
                case "NS":
                    return ["Contract Date", "Closing Date", "Requisition Date", "Conditional Until Date", "Lawyer Review Date"]
                default:
                    return ["Contract Date", "Closing Date", "Requisition Date", "Conditional Until Date"];
            }
        }
        if(DataReaderObj || newFileCreation){
        const docContractDate = (appCode === "NS" || appCode === "SK")? responseData?.result?.ContractDate : responseData?.result?.AgreementDate;
        const docClosingDate = responseData?.result?.ClosingDate;
        const docRequisitionDate = responseData?.result[`${['BC', 'MB'].indexOf(appCode) > -1 ? "Possession": "Requisition"}Date`];
        const conditionalUnitField = appCode === "SK"? "OpenToAcceptanceDate":"ConditionalUntilDate";
        const docConditionalUntilDate = appCode === "MB"?"":responseData?.result[conditionalUnitField];
        const docLawyerReviewDate = appCode === "NS" ? responseData?.result?.LawyerReviewDate : "";
    
        const inFileContractDate = DataReaderObj?.getContractDate();
        const inFileClosingDate = DataReaderObj?.getClosingDate();
        const inFileRequisitionDate = DataReaderObj?.getRequisionDate();
        const inFileConditionalUntilDate = DataReaderObj?.getConditionalUntilDate();
        const inFilePossessionDate = DataReaderObj?.getEarlyPossessionDate();
        const inFileLawyerReviewDate = DataReaderObj?.getLawyerReviewDate();
    
        const headers = contractHeaders(appCode);
        const docArr = appCode === "NS" 
            ? [docContractDate, docClosingDate, docRequisitionDate, docConditionalUntilDate, docLawyerReviewDate]
            : [docContractDate, docClosingDate, docRequisitionDate, docConditionalUntilDate];
        const inFileArr = appCode === "BC" || appCode === "MB"
            ? [inFileContractDate, inFileClosingDate, inFilePossessionDate, inFileConditionalUntilDate]
            : appCode === "NS"
            ? [inFileContractDate, inFileClosingDate, inFileRequisitionDate, inFileConditionalUntilDate, inFileLawyerReviewDate]
            : [inFileContractDate, inFileClosingDate, inFileRequisitionDate, inFileConditionalUntilDate];
        const valTypes = Array.from({ length: headers.length }, () => Array(3).fill("string"));
        valTypes.forEach((typeArray, index) => {typeArray.fill("date", 1);});
    
        return { fields: headers.map((header, index) => [ header, docArr[index]?.trim() || '', inFileArr[index]?.trim() || '']), valTypes };
        }
    }

    static getSellerBrokerageData(responseData: any, DataReaderObj: RPWDataReader, newFileCreation: boolean) {
        if(DataReaderObj || newFileCreation){
        if(DataReaderObj.getAppCode() === "BC" && responseData?.result){
            // A CIR lists each office's own address, so don't copy the listing address onto the buyer side.
            if(!isConveyancerInstructionReport(responseData)){
                responseData.result.BuyerBrokerageAddress = responseData.result.ListingBrokerageAddress || responseData.result.BuyerBrokerageAddress;
            }
            responseData.result.ListingBrokerage = responseData.result.AgencyDisclosureSellerAgentBrokerage;
            responseData.result.ListingBrokerageBrokerName = responseData.result.AgencyDisclosureSellerAgent;
        }
        if(DataReaderObj.getAppCode() === "SK" && responseData?.result){
            responseData.result.ListingBrokerage = responseData.result.SellersBrokerageName;
            responseData.result.ListingBrokerageBrokerName = responseData.result.SellersBrokerageSalesperson;
            responseData.result.ListingBrokeragePhoneNumber = responseData.result.SellersBrokerageTelephone;
            responseData.result.ListingBrokerageAddress = responseData.result.SellersBrokerageAddress;
        }
        const docListingAgentBrokerage = responseData?.result?.ListingBrokerage;
        const docListingAgentName = responseData?.result?.ListingBrokerageBrokerName;
        const docListingAgentPhoneNumber = responseData?.result?.ListingBrokeragePhoneNumber;

        const realEstateAgent = DataReaderObj?.getRealEstateAgent();
        const inFileListingAgentName = realEstateAgent?.getAttribute("Name");
        const inFileListingAgentBrokerage = realEstateAgent?.getAttribute("Company");
        const inFileListingAgentPhoneNumber = realEstateAgent?.getAttribute("Phone");
    
        let headers = ["Agent", "Brokerage", "Phone No."];
        let docArr = [docListingAgentName, docListingAgentBrokerage, docListingAgentPhoneNumber];
        let inFileArr = [inFileListingAgentName, inFileListingAgentBrokerage, inFileListingAgentPhoneNumber];
        let valTypes = Array.from({ length: headers.length }, () => Array(3).fill("string"));
        valTypes[2] = ["string", "phone", "phone"];
    
        if(['AB', 'MB'].indexOf(DataReaderObj.getAppCode()) > -1){
            const docListingAgentEmail = responseData?.result?.ListingBrokerageEmail;
            const inFileListingAgentEmail = realEstateAgent?.getAttribute("Email");
            headers.push("Email");
            docArr.push(docListingAgentEmail);
            inFileArr.push(inFileListingAgentEmail);
            valTypes.push(Array(3).fill("string"));
        }
        if(['AB', 'MB', 'SK'].indexOf(DataReaderObj.getAppCode()) > -1){
            const docListingAgentAddress = responseData?.result?.ListingBrokerageAddress;
            const inFileListingAgentAddress = realEstateAgent?.getAttribute("Address");
            headers.push("Address");
            docArr.push(docListingAgentAddress);
            inFileArr.push(inFileListingAgentAddress);
            valTypes.push(Array(3).fill("string"));
        }
        if(DataReaderObj.getAppCode() === "AB"){
            const docListingAgentFax = responseData?.result?.ListingBrokerageFax;
            const inFileListingAgentFax = realEstateAgent?.getAttribute("Fax");
            headers.push("Fax");
            docArr.push(docListingAgentFax);
            inFileArr.push(inFileListingAgentFax);
            valTypes.push(Array(3).fill("string"));
        }
        // ns purchase files don't have any agents
        if(DataReaderObj.getAppCode() === "NS" && DataReaderObj.getPSM() === "P"){
            return {fields: [], valTypes: []}
        }

        return { fields: headers.map((header, index) => [ header, docArr[index] || '', inFileArr[index] || '']), valTypes };
        }
    }
    
    static getBuyerBrokerageData(responseData: any, DataReaderObj: RPWDataReader, newFileCreation: boolean) {
        if(DataReaderObj || newFileCreation){
        if(DataReaderObj.getAppCode() === "BC" && responseData?.result){
            responseData.result.BuyerBrokerage = responseData.result.AgencyDisclosureBuyerAgentBrokerage;
            responseData.result.BuyerBrokerageBrokerName = responseData.result.AgencyDisclosureBuyerAgent;
            // A CIR lists the selling office's own phone, so don't overwrite it with the listing phone.
            if(!isConveyancerInstructionReport(responseData)){
                responseData.result.BuyerBrokeragePhoneNumber = responseData.result.ListingBrokeragePhoneNumber;
            }
        }
        if(DataReaderObj.getAppCode() === "SK" && responseData?.result){
            responseData.result.BuyerBrokerage = responseData.result.BuyersBrokerageName;
            responseData.result.BuyerBrokerageBrokerName = responseData.result.BuyersBrokerageSalesperson;
            responseData.result.BuyerBrokeragePhoneNumber = responseData.result.BuyersBrokerageTelephone;
            responseData.result.BuyerBrokerageAddress = responseData.result.BuyersBrokerageAddress;
        }
        const docBuyerBrokerage = responseData?.result?.BuyerBrokerage;
        const docBuyerBrokerName = responseData?.result?.BuyerBrokerageBrokerName;
        const docBuyerBrokeragePhoneNumber = responseData?.result?.BuyerBrokeragePhoneNumber;
    
        const realEstateAgent = (DataReaderObj.getAppCode() === "ON") ? DataReaderObj?.getRealEstateAgent(): DataReaderObj?.getPurchaserRealEstateAgent();
        const inFileBuyerBrokerName = realEstateAgent?.getAttribute("Name");
        const inFileBuyerBrokerage = realEstateAgent?.getAttribute("Company");
        const inFileBuyerBrokeragePhoneNumber = realEstateAgent?.getAttribute("Phone");
    
        let headers = ["Agent", "Brokerage", "Phone No."];
        let docArr = [docBuyerBrokerName, docBuyerBrokerage, docBuyerBrokeragePhoneNumber];
        let inFileArr = [inFileBuyerBrokerName, inFileBuyerBrokerage, inFileBuyerBrokeragePhoneNumber];
        let valTypes = Array.from({ length: headers.length }, () => Array(3).fill("string"));
        valTypes[2] = ["string", "phone", "phone"];
    
        if(['AB', 'MB'].indexOf(DataReaderObj.getAppCode()) > -1){
            const docBuyerBrokerageEmail = responseData?.result?.BuyerBrokerageEmail;
            const inFileBuyerBrokerageEmail = realEstateAgent?.getAttribute("Email");
            headers.push("Email");
            docArr.push(docBuyerBrokerageEmail);
            inFileArr.push(inFileBuyerBrokerageEmail);
            valTypes.push(Array(3).fill("string"));
        }
        if(['AB', 'BC', 'MB', 'SK'].indexOf(DataReaderObj.getAppCode()) > -1){
            const docBuyerBrokerageAddress = responseData?.result?.BuyerBrokerageAddress;
            const inFileBuyerBrokerageAddress = realEstateAgent?.getAttribute("Address");
            headers.push("Address");
            docArr.push(docBuyerBrokerageAddress);
            inFileArr.push(inFileBuyerBrokerageAddress);
            valTypes.push(Array(3).fill("string"));
        }
        if(DataReaderObj.getAppCode() === "AB"){
            const docBuyerBrokerageFax = responseData?.result?.BuyerBrokerageFax;
            const inFileBuyerBrokerageFax = realEstateAgent?.getAttribute("Fax");
            headers.push("Fax");
            docArr.push(docBuyerBrokerageFax);
            inFileArr.push(inFileBuyerBrokerageFax);
            valTypes.push(Array(3).fill("string"));
        }
        // ns purchase files don't have any agents
        if(DataReaderObj.getAppCode() === "NS" && DataReaderObj.getPSM() === "P"){
            return {fields: [], valTypes: []}
        }
        
        return { fields: headers.map((header, index) => [ header, docArr[index] || '', inFileArr[index] || '']), valTypes };
        }
    }
    
    static getChattels(responseData: any) {
        return responseData?.result?.Chattels || [];
    }
    
    static getFixtures(responseData: any) {
        return responseData?.result?.Fixtures || [];
    }
    
    static getRentals(responseData: any) {
        return responseData?.result?.Rentals || [];
    } 

    static async logImportInfo(SESSION_INFO: lddGlobalSessionInfo, appId: string, LddDocLink: string, importInfo: any, docType: string) {
        const { appProvinceShort, firm, sid } = SESSION_INFO;
        const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}/${appId}`;
        try{
            const response = await fetch(`${RWAPI}/filedrive/logImport?SessionID=${sid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    importInfo: importInfo,
                    docType: docType,
                    LddDocLink: LddDocLink
                })
            });
            const data = await response.json();
            return data;
        } catch(error){
            console.error("[RenAI]: Error logging import info", error);
            return null;
        }
    }

    static async handleImportInfo(
        docType: string, 
        importInfo: any, 
        responseData: RenAIResponseData, 
        DataReaderObj: RPWDataReader, 
        selectedLawyer: any, 
        SESSION_INFO: lddGlobalSessionInfo
    ) {
        if(docType){
            switch(docType.toLowerCase()) {
                case 'aps':
                    if(SESSION_INFO.appProvinceShort === "BC" && importInfo.buyers && importInfo?.buyers["Buyer Address"]){ // do this before processing aps names
                        DataReaderObj.setPurchaserAddress(importInfo.buyers["Buyer Address"]);
                    }
                    // Parse names for BC before processing
                    if (["BC", "MB", "SK"].indexOf(SESSION_INFO.appProvinceShort) > -1) {
                        importInfo = this.parseNamesForAPS(importInfo);
                    }

                    if(importInfo.property) {
                        importInfo.property = {
                            ...importInfo.property,
                            // Address: `${importInfo.property['Street Number']}${importInfo.property['Suffix']} ${importInfo.property['Street Name']}, ${importInfo.property['City/Town']}, ${SESSION_INFO.appProvince} ${importInfo.property['Postal Code']}`,
                            StreetNum: importInfo.property['Street Number'],
                            StreetName: importInfo.property['Street Name'],
                            Suffix: importInfo.property['Suffix'],
                            PostalCode: importInfo.property['Postal Code'],
                            City: importInfo.property['City/Town'],
                            PropertyType: responseData?.result?.docUploadPropertyType
                        };
                        if(importInfo && importInfo.property && importInfo.property['City/Town']) {
                            if(await LoadMunicByName(importInfo.property['City/Town'], (DataReaderObj as any).livedataRoot, SESSION_INFO.sid))
                                ResetMunicRecord((DataReaderObj as any).livedataRoot, SESSION_INFO.sid);
                            else
                            {
                                alert("Cannot load the information of municipality '" + importInfo.property['City/Town'] + "'");
                                delete importInfo.property['City/Town'];
                                delete importInfo.property['City'];
                            }
                        }
                        if(importInfo.property['New Home'] && importInfo.property['New Home'] === "true"){
                            DataReaderObj.setNewHome("0");
                        }
                        if(importInfo.property['PID']){
                            DataReaderObj.setPID(importInfo.property['PID'])
                        }
                        
                        // Find coordinates of the property for Google Map by using function from legacy code
                        const address = `${importInfo.property['Street Number']}${importInfo.property['Suffix']} ${importInfo.property['Street Name']}, ${importInfo.property['City/Town']}, ${SESSION_INFO.appProvince} ${importInfo.property['Postal Code']}`;
                        const location: any = await runInnerFunction('ReactShell.findGmapCoordinateByAddress', address)
                        const lastGmapAddressKey = `${importInfo.property['Street Number']}${importInfo.property['Street Name']}${importInfo.property['City/Town']}`;
                        if (location && typeof location.lat === 'number' && typeof location.lng === 'number') {
                            // Set coordinates on the Property node
                            try {
                                DataReaderObj.setCoordinates(location.lat, location.lng);
                                DataReaderObj.setLastGmapAddressKey(lastGmapAddressKey);
                            } catch (e) {
                                console.error('[RenAI Import]: Error setting lat/lng on Property node', e);
                            }

                            // Also push into Redux so the React map state updates immediately
                            try {
                                const coordStr = `(${location.lat}, ${location.lng})`;
                                store.dispatch(setPropertyCoordinate(coordStr));
                            } catch (e) {
                                console.error('[RenAI Import]: Error updating PropertyCoordinate in Redux', e);
                            }
                        } else {
                            console.log('[RenAI Import]: Location not available', address);
                        }
                    }
                    if(importInfo.contract) {
                        if((SESSION_INFO.appProvinceShort === "MB" && responseData.result['BuyersConditionsDate']) || importInfo.contract['Conditional Until Date']){
                            importInfo.contract.OfferConditional = 1;
                        }
                    }
                    if (importInfo.priceDeposits) {
                        for (const key in importInfo.priceDeposits) {
                            if (typeof importInfo.priceDeposits[key] === 'string') {
                                importInfo.priceDeposits[key] = importInfo.priceDeposits[key].replace(/,/g, '');
                            }
                        }
                    }
                    if(importInfo.otherSideLegalRepresentation && Object.keys(importInfo.otherSideLegalRepresentation).length > 0){
                        runInnerFunction('ReactShell.reactDeleteSolicitor', null);
                        if(selectedLawyer) {
                            setThirdPartyData(
                                { ...selectedLawyer, 
                                    Name: importInfo.otherSideLegalRepresentation.Name, 
                                    Address: importInfo.otherSideLegalRepresentation.Address, 
                                    DirectPhone: importInfo.otherSideLegalRepresentation.DirectPhone, 
                                    FirmEmail: importInfo.otherSideLegalRepresentation.Email, 
                                    Fax: importInfo.otherSideLegalRepresentation.Fax 
                                }, 
                                "Solicitor"
                            );
                        }
                        // use new address format for manitoba since that is the default
                        if(["MB", "SK"].indexOf(SESSION_INFO?.appProvinceShort) > -1){
                            let newAddr = runInnerFunction("ParseAddressCanadian", importInfo.otherSideLegalRepresentation.Address);
                            importInfo.otherSideLegalRepresentation.newAddressFormat = "1";
                            newAddr.forEach((line: string, index: number) => {
                                // need to end with another character since mapper will try to make new nodes if fields of same name end with 1, 2, 3, etc
                                importInfo.otherSideLegalRepresentation[`newAddressLine${index + 1}.`] = line;
                            })
                        }

                    }
                    let taxHeaderName = `${["AB", "BC", "MB"].indexOf(SESSION_INFO?.appProvinceShort) > -1 ? "GST":"HST"} Applicable`
                    if(importInfo.priceDeposits && importInfo.priceDeposits[taxHeaderName]) {
                        importInfo.priceDeposits[taxHeaderName] = importInfo.priceDeposits[taxHeaderName] === 'true' ? 1 : 0;
                        // no tax for NS for now
                        if(SESSION_INFO.appProvinceShort === "NS"){
                            delete importInfo.priceDeposits[taxHeaderName];
                        }
                    }
                    if(importInfo.listingAgent && Object.keys(importInfo.listingAgent).length > 0){
                        runInnerFunction('ReactShell.reactDeleteRealEstateAgent', null);
                    }
                    if(importInfo.listingAgent && (importInfo.listingAgent['Agent'] || importInfo.listingAgent['Brokerage'])){
                        importInfo.listingAgent['CompanyAgentName'] = `${importInfo.listingAgent['Brokerage'] || ''} - - ${importInfo.listingAgent['Agent'] || ''}`
                    }
                    if(importInfo.coopAgent && Object.keys(importInfo.coopAgent).length > 0){
                        if(SESSION_INFO?.appProvinceShort === "ON"){
                            runInnerFunction('ReactShell.reactDeleteRealEstateAgent', null);
                        } else {
                            runInnerFunction('ReactShell.reactDeletePurchaserRealEstateAgent', null);
                        }
                    }
                    if(importInfo.coopAgent && (importInfo.coopAgent['Agent'] || importInfo.coopAgent['Brokerage'])){
                        importInfo.coopAgent['CompanyAgentName'] = `${importInfo.coopAgent['Brokerage'] || ''} - - ${importInfo.coopAgent['Agent'] || ''}`
                    }
                    DataReaderObj?.setFieldsFromRecord(importInfo);
                    break;
                default:
                    return null;
            } 
        }
        
    }

    static parseNamesForAPS(importInfo: any): any {
        const parsedImportInfo = { ...importInfo };

        // Parse buyers
        if (parsedImportInfo.buyers) {
            const parsedBuyers: Record<string, any> = {};

            for (const [key, value] of Object.entries(parsedImportInfo.buyers)) {
                if (typeof value === 'string' && value.trim()) {
                    // Extract index from key (e.g., "Buyer 1" -> 1, "Buyer 3" -> 3)
                    if(key === "Buyer Address") continue;
                    const match = key.match(/(\d+)$/);
                    const buyerIndex = match ? match[1] : '1';
                    
                    const nameParts = parseNameString(value);
                    parsedBuyers[`Buyer Firstname ${buyerIndex}`] = nameParts.firstName;
                    parsedBuyers[`Buyer Middlename ${buyerIndex}`] = nameParts.middleName;
                    parsedBuyers[`Buyer Lastname ${buyerIndex}`] = nameParts.lastName;
                }
            }
            parsedImportInfo.buyers = parsedBuyers;
        }

        // Parse sellers
        if (parsedImportInfo.sellers) {
            const parsedSellers: Record<string, any> = {};

            for (const [key, value] of Object.entries(parsedImportInfo.sellers)) {
                if (typeof value === 'string' && value.trim()) {
                    // Extract index from key (e.g., "Seller 1" -> 1, "Seller 3" -> 3)
                    const match = key.match(/(\d+)$/);
                    const sellerIndex = match ? match[1] : '1';
                    
                    const nameParts = parseNameString(value);
                    parsedSellers[`Seller Firstname ${sellerIndex}`] = nameParts.firstName;
                    parsedSellers[`Seller Middlename ${sellerIndex}`] = nameParts.middleName;
                    parsedSellers[`Seller Lastname ${sellerIndex}`] = nameParts.lastName;
                }
            }
            parsedImportInfo.sellers = parsedSellers;
        }

        return parsedImportInfo;
    }

}

export function getDocTypeTitle(docType: string) {
    switch(docType) {
        case 'aps':
            return 'Agreement of Purchase and Sale';
        default:
            return 'Mortgage Instructions';
    }
}

export function isValidEmail(email: string): boolean {
    if (typeof email !== 'string' || !email.trim()) {
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email.trim()) && email.trim().length <= 254;
  }
  
  export function isValidPhone(phone: string): boolean {
    if (typeof phone !== 'string' || !phone.trim()) {
      return false;
    }
    const numericChars = phone.replace(/\D/g, '');
    return numericChars.length >= 10;
  }
  
  export function isValidPrice(price: string): boolean {
    if (typeof price !== 'string' || !price.trim()) {
      return false;
    }
    const trimmedPrice = price.trim();
    const periodCount = (trimmedPrice.match(/\./g) || []).length;
    if (periodCount > 1) {
      return false;
    }
    const hasDigit = /\d/.test(trimmedPrice);
    return hasDigit;
  }

export function getDocTypeMessage(docType: string, appCode: string) {
    switch(docType) {
        case 'aps':
            return getAPSDisplayShort(appCode).toLowerCase()
        default:
            return 'mortgage instructions document';
    }
}

export const checkFileSize = (file: File): { isValid: boolean; errorMessage?: string } => {
    const MAX_FILE_SIZE = 10 * Math.pow(2, 20); // 10 MB
    if (file.size > MAX_FILE_SIZE) {
        return {
            isValid: false,
            errorMessage: `This file exceeds the upload limit of ${MAX_FILE_SIZE / Math.pow(2, 20)}MB. Please try another file.`
        };
    }
    return { isValid: true };
};

export const validatePdfFile = (file: File | null): { isValid: boolean; errorMessage?: string } => {
    if (!file) {
        return {
            isValid: false,
            errorMessage: "Please select a file to upload."
        };
    }

    if (file.type !== "application/pdf") {
        return {
            isValid: false,
            errorMessage: "Please upload a PDF document."
        };
    }

    return checkFileSize(file);
};

export const processPdfFile = async (
    file: File,
    PDFFileName: string,
    callbacks: UploadCallbacks
): Promise<void> => {
    try {
        const validationResult = validatePdfFile(file);
        if (!validationResult.isValid) {
            callbacks.onFailure(validationResult.errorMessage);
            return;
        }

        const images = await convertPdfToImages(file, callbacks.onProgress);
        callbacks.onSuccess(images);
    } catch (error) {
        console.error('Error processing PDF:', error);
        callbacks.onFailure();
    }
};

interface SessionInfo {
    appProvinceShort: string;
    firm: {
        id: number;
    };
    sid: string;
}

export const parseRenAIResponse = (data: any, appProvinceShort?: string) => {
    const parsedResponse: Record<string, any> = {};

    for (const [key, valueObj] of Object.entries(data?.result)) {
        if (valueObj && typeof valueObj === 'object' && 'value' in valueObj && 'type' in valueObj) {
            let value = valueObj.value as any;
            if (valueObj.type === 'money') {
                value = value.toString().replaceAll("$", '').replaceAll(" ", '').replaceAll(",", '');
            }
            parsedResponse[key] = value;
        }
    }

    if(parsedResponse){
        if(parsedResponse.CompletionDate){
            parsedResponse.ClosingDate = parsedResponse.CompletionDate;
        } else {
            parsedResponse.ClosingDate = '';
        }
        if(parsedResponse.RequisitionDate || parsedResponse.TitleSearchRequisitionDate){
            parsedResponse.RequisitionDate = parsedResponse.RequisitionDate > "" ? parsedResponse.RequisitionDate : parsedResponse.TitleSearchRequisitionDate;
        } else {
            parsedResponse.RequisitionDate = '';
        }
        if (appProvinceShort === "ON") {
            parsedResponse.ConditionalUntilDate = "";
        } else if (appProvinceShort === "AB") {
            parsedResponse.ConditionalUntilDate = parsedResponse.BuyersConditionsDate || "";
        } else if (parsedResponse.IrrevocabilityDate) {
            parsedResponse.ConditionalUntilDate = parsedResponse.IrrevocabilityDate;
        } else {
            parsedResponse.ConditionalUntilDate = "";
        }
        if(parsedResponse.StreetName && parsedResponse.StreetNum){
            parsedResponse.propertyAddress = `${parsedResponse.StreetNum ? parsedResponse.StreetNum + " " : ''}` +
                                             `${parsedResponse.StreetName}${parsedResponse.StreetSuffix ? '' : ','} ${parsedResponse.StreetSuffix ? parsedResponse.StreetSuffix + "," : ''} ${parsedResponse.City}, ` +
                                             `${parsedResponse.Province} ${parsedResponse.PostalCode}`;
        } else {
            parsedResponse.propertyAddress = '';
        }
    }

    data.result = parsedResponse;

    return data;
}

export const getAnalyzedDocuments = async (sessionInfo: SessionInfo, appId: string) => {
    const { appProvinceShort, firm, sid } = sessionInfo;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}/${appId}`;
    try{
        const response = await fetch(`${RWAPI}/filedrive/getAnalyzedDocuments?SessionID=${sid}`);
        const data = await response.json();
        return data;
    } catch(error){
        console.error("[RenAI]: Error getting analyzed documents", error);
        return null;
    }
}

export const deleteAnalyzedDocument = async (sessionInfo: SessionInfo, appId: string, docId: string, docType: string) => {
    const { appProvinceShort, firm, sid } = sessionInfo;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try{
        const response = await fetch(`${RWAPI}/${appId}/filedrive/deleteAnalyzedDocument?SessionID=${sid}`, {       
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ docId, docType })
    });
        const data = await response.json();
        return data;
    } catch(error){
        console.error("[RenAI]: Error deleting analyzed document", error);
        return null;
    }
}

export const uploadToStorage = async (
    file: File,
    images: string[],
    sessionInfo: SessionInfo,
    appId: string,
    psm: string,
    onSuccess: (data: any) => void,
    onFailure: (errorCode?: string) => void
): Promise<void> => {
    const { appProvinceShort, firm, sid } = sessionInfo;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}/${appId}`;
    const docId = uuidv4();
    const STORAGE_PATH = `Analyzer/APS/${docId}`;
    const docName = `${docId}.pdf`;

    if (!images || images.length === 0) {
        onFailure('No images were generated from the PDF');
        return;
    }

    try {
        const arrayBuffer = await file.arrayBuffer();
        const step1Res = await fetch(
            `${RWAPI}/filedrive/storageDocUpload?SessionID=${sid}&name=${encodeURIComponent(docName)}&path=${encodeURIComponent(STORAGE_PATH)}`,
            {
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
                method: 'POST',
                body: arrayBuffer
            }
        );

        if (!step1Res.ok) {
            throw new Error('Failed to upload PDF document.');
        }

        const imageUploads = images.map(async (imageData, index) => {
            const imageName = `${docId}_page${index + 1}.png`;
            const imageBlob = convertBase64ToBlob(imageData);
                if (!imageBlob) {
                    throw new Error(`Failed to convert image ${index + 1} from base64 to blob`);
                }
                const arrayBuffer = await imageBlob.arrayBuffer();
                const imageRes = await fetch(
                    `${RWAPI}/filedrive/storageImgUpload?SessionID=${sid}&name=${encodeURIComponent(imageName)}&path=${encodeURIComponent(STORAGE_PATH + '/img')}`,
                    {
                        headers: {
                            'Content-Type': 'image/png',
                        },
                        method: 'POST',
                        body: arrayBuffer
                    }
                );

                if (!imageRes.ok) {
                    const errorText = await imageRes.text();
                    let errorMessage = `Failed to upload image ${index + 1}`;
                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.result && errorJson.result.msg) {
                            errorMessage = errorJson.result.msg;
                        }
                    } catch (e) {
                        if (errorText) {
                            errorMessage = errorText;
                        }
                    }
                    throw new Error(errorMessage);
                }

                const responseData = await imageRes.json();
                if (!responseData.ok) {
                    throw new Error(responseData.result?.msg || `Upload failed for image ${index + 1}`);
                }
        });

        await Promise.all(imageUploads);

        const analysisRes = await fetch(
            `${RWAPI}/filedrive/autogen?SessionID=${sid}&name=${encodeURIComponent(docName)}&path=${encodeURIComponent(STORAGE_PATH)}`,
            {
                method: 'POST',
                body: JSON.stringify({
                    fileName: file?.name,
                    size: file?.size,
                    lastModified: file?.lastModified,
                    docType: 'aps',
                    requestType: 'extraction',
                    psm: psm
                })
            }
        );

        if (!analysisRes.ok) {
            const errorResponse = await analysisRes.json();
            throw new Error(JSON.stringify({
                message: errorResponse.error || 'Failed to get analyzed data',
                result: errorResponse?.result
            }));
        }

        const responseBody = await analysisRes.json();
        const data = responseBody.result;
        data.base64Content = URL.createObjectURL(file);
        data.id = docId;
        onSuccess(data);

    } catch (error) {
        onFailure("Failed to analyze document");
    }
};

const convertBase64ToBlob = (base64Data: string): Blob | null => {
    try {
        let cleanBase64 = base64Data;
        if (base64Data.startsWith('data:image/png;base64,')) {
            cleanBase64 = base64Data.split(',')[1];
        } else if (base64Data.startsWith('data:')) {
            const commaIndex = base64Data.indexOf(',');
            if (commaIndex !== -1) {
                cleanBase64 = base64Data.substring(commaIndex + 1);
            }
        }
        if (!cleanBase64 || cleanBase64.length === 0) {
            return null;
        }

        atob(cleanBase64);

        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        return blob;

    } catch (error) {
        console.error('Error in base64 to blob conversion:', error);
        return null;
    }
};

export function getDisplayDateTimeFromMS(dateTime: string) {
    return new Date(dateTime).toLocaleString(
        'en-US', { 
            // weekday: 'short', 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit', 
            // second: '2-digit', 
            hour12: false 
        }
    )
}

export const convertPdfToImages = async (
    file: File, 
    onProgress?: (currentPage: number, totalPages: number) => void
): Promise<string[]> => {
    try {
        const arrayBuffer = await file.arrayBuffer();

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pageImages: string[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            try {
                if (onProgress) {
                    onProgress(pageNum, pdf.numPages);
                }
                
                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale: 3.0 });
                
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                
                if (!context) {
                    throw new Error(`Failed to get 2D context for page ${pageNum}`);
                }
                
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };

                await page.render(renderContext).promise;
                const imageDataUrl = canvas.toDataURL('image/png');
                if (!imageDataUrl || imageDataUrl.length < 100) {
                    throw new Error(`Generated image data for page ${pageNum} is too small or invalid`);
                }
                pageImages.push(imageDataUrl);

            } catch (pageError) {
                console.error(`Error processing page ${pageNum}:`, pageError);
                throw new Error(`Failed to process page ${pageNum}: ${pageError.message}`);
            }
        }

        if (pageImages.length === 0) {
            throw new Error('No images were generated from the PDF');
        }
        
        return pageImages;
    } catch (error) {
        console.error('Error converting PDF to images:', error);
        throw error;
    }
}; 

export const getAPSDisplayName = (appCode: string) => {
    switch(appCode){
        case "BC":
        case "AB":
            return "Contract of Purchase and Sale";
        case "MB":
        case "SK":
            return "Offer to Purchase";
        default:
            return "Agreement of Purchase and Sale";
    }
}

export const getAPSDisplayShort = (appCode: string) => {
    switch(appCode){
        case "BC":
        case "AB":
            return "Contract";
        case "MB":
        case "SK":
            return "Offer";
        default:
            return "Agreement";
    }
}

export const getAPSDisplayAbbr = (appCode: string) => {
    switch(appCode){
        case "AB":
            return "Purchase Contract";
        case "BC":
            return "CPS";
        case "MB":
        case "SK":
            return "OTP";
        default:
            return "APS";
    }
}

export const checkDatePickerDate = (date: string) => {
    let onlyDate = date.split("T")[0];
    return onlyDate.match(/^\d{4}-\d{2}-\d{2}$/)? onlyDate : "";
}